import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, defer, timer, merge, fromEvent } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, finalize, map, shareReplay, switchMap, tap, timeout, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage, AngularFireUploadTask } from '@angular/fire/compat/storage';
import { CrudService } from './crud.service';
import { SoundService } from './sound.service';
import { NotificacionService } from './notificacion.service';
import { UrlServer } from '../helpers/UrlServer';
import { getUserId } from '../helpers/utilities';

import { normalizeChat, mergeChatMessages } from '../helpers/chat-reliability';
export { normalizeChat, mergeChatMessages } from '../helpers/chat-reliability';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private selected = new BehaviorSubject<any[]>([]);
  selectedChatOpen$ = this.selected.asObservable();
  selectedChats: any[] = [];
  notificaciones = {own: 0, other: 0};
  private notifications = new BehaviorSubject<number>(0);
  private sessionEnded = new Subject<void>();
  private restart = new BehaviorSubject<number>(0);
  readonly connection$ = new BehaviorSubject<string>('connecting');
  readonly alerts$ = new Subject<any>();
  private baseline = new Map<string, string>();
  private initialized = false;
  private session = 0;
  private scopeStates = new Map<string, string>();
  private knownIds = new Map<string, string[]>();
  userId = 0;
  filtro = 'mios';
  private readonly queue$ = this.poll({}).pipe(
    tap(() => this.soundService.startNotificationSession()),
    map(data => (data.chats || []).map(normalizeChat)),
    tap(chats => this.checkNovelty(chats)),
    distinctUntilChanged((a,b) => JSON.stringify(a) === JSON.stringify(b)),
    shareReplay({bufferSize: 1, refCount: true})
  );
  constructor(private http: HttpClient, public firestore: AngularFirestore,
    private storage: AngularFireStorage, private crudService: CrudService,
    private soundService: SoundService, private notificacionService: NotificacionService) {}
  private state(scope: string, state: string): void {
    this.scopeStates.set(scope, state);
    const values = [...this.scopeStates.values()];
    const aggregate = values.includes('blocked') ? 'blocked' : values.includes('offline') ? 'offline' : values.includes('retrying') ? 'retrying' : values.includes('connecting') ? 'connecting' : 'live';
    if (this.connection$.value !== aggregate) this.connection$.next(aggregate);
  }
  private poll(params: any, requestParams: () => any = () => params): Observable<any> {
    const scope = JSON.stringify(params);
    return defer(() => this.restart.pipe(switchMap(() => {
      let failures = 0; let nextAllowed = 0;
      this.state(scope, 'connecting');
      return merge(timer(0, 2000), fromEvent(window, 'online').pipe(tap(() => nextAllowed = 0))).pipe(
        exhaustMap(() => {
          if (Date.now() < nextAllowed) return EMPTY;
          return defer(() => this.http.get<any>('chatRealtime', {params: requestParams(), headers: {'X-Chat-Background': '1'}})).pipe(
            timeout(15000), tap(() => { failures = 0; this.state(scope, 'live'); }),
            catchError(error => {
              failures++; const blocked = error.status === 401 || error.status === 403;
              nextAllowed = blocked ? Infinity : Date.now() + Math.min(30000, 1000 * Math.pow(2, failures));
              this.state(scope, blocked ? 'blocked' : navigator.onLine ? 'retrying' : 'offline');
              console.warn('chat_subscription', {status: error.status || 0}); return EMPTY;
            })
          );
        }),
        finalize(() => { this.scopeStates.delete(scope); this.state('', 'live'); this.scopeStates.delete(''); })
      );
    }), takeUntil(this.sessionEnded)));
  }
  reconnect(): void { this.connection$.next('connecting'); this.restart.next(this.restart.value + 1); }
  stopSession(): void { this.soundService.stopNotificationSession(); this.sessionEnded.next(); this.connection$.next('idle'); this.selectedChats = []; this.selected.next([]); this.baseline.clear(); this.knownIds.clear(); this.initialized = false; this.session = 0; }
  private checkNovelty(chats: any[]): void {
    const user = getUserId();
    if (this.session !== user) {
      this.session = user; this.initialized = false;
      try { this.baseline = new Map(JSON.parse(sessionStorage.getItem('chat-alerts-' + user) || '[]')); } catch { this.baseline.clear(); }
    }
    for (const chat of chats) {
      const key = String(chat.idChat); const incoming = String(chat.lastIncomingMessageId || '');
      const previous = this.baseline.get(key);
      if ((this.initialized || previous !== undefined) && incoming && previous !== incoming && Number(chat.notificacionAgente) > 0 && this.soundService.isNotificationOwner()) {
        const notificationKey = 'chat-notified-' + user + '-' + key;
        let already = false; try { already = localStorage.getItem(notificationKey) === incoming; if (!already) localStorage.setItem(notificationKey, incoming); } catch {}
        if (!already) { this.soundService.play('newChat'); this.alerts$.next(chat); }
      }
      if (!this.initialized || previous === incoming || Number(chat.notificacionAgente) === 0 || this.soundService.isNotificationOwner()) this.baseline.set(key, incoming);
    }
    this.initialized = true;
    try { sessionStorage.setItem('chat-alerts-' + user, JSON.stringify([...this.baseline.entries()].slice(-2000))); } catch { /* Storage may be unavailable. */ }
  }
  returnAllChats(): Observable<any[]> { return this.queue$.pipe(map(chats => chats.filter((chat: any) => chat.chatEstatus !== 'CERRADO' && chat.agente?.id !== getUserId()))); }
  returnMyChats(): Observable<any[]> { return this.queue$.pipe(map(chats => chats.filter((chat: any) => chat.chatEstatus !== 'CERRADO' && chat.agente?.id === getUserId()))); }
  getChatsCount(): Observable<any[]> { return this.queue$.pipe(map(chats => chats.filter((chat: any) => chat.chatEstatus !== 'CERRADO' && Number(chat.notificacionAgente) > 0))); }
  setNotficaciones(count: number, filter: number): void { if (filter) this.notificaciones.own = count; else this.notificaciones.other = count; this.notifications.next(this.notificaciones.own + this.notificaciones.other); }
  returnNotificaciones(): Observable<number> { return this.notifications.asObservable(); }
  newChatView(chat: any): void {
    const existing = this.selectedChats.find(item => item.idChat === chat.idChat);
    const remaining = this.selectedChats.filter(item => item.idChat !== chat.idChat);
    const max = matchMedia('(max-width: 600px)').matches ? 1 : 3;
    this.selectedChats = [...remaining, {...existing, ...chat}].slice(-max); this.selected.next(this.selectedChats);
  }
  closeChatView(idChat: string): void { this.selectedChats = this.selectedChats.filter(chat => String(chat.idChat) !== String(idChat)); this.selected.next(this.selectedChats); }
  returnSelectedChats(): Observable<any[]> { return this.selected.asObservable(); }
  private scope(path: string): any { return path.startsWith('A') ? {idChat: path.slice(1)} : {ownerId: path}; }
  registerMessageIds(path: string, messages: any[]): void { this.knownIds.set(path, messages.slice(-500).map(message => String(message.idChatMessage))); }
  watchConversation(path: string): Observable<any> {
    const params: any = this.scope(path);
    return defer(() => {
      let latest = 0;
      return this.poll(params, () => ({...params, afterMessageId: String(latest), knownMessageIds: (this.knownIds.get(path) || []).join(',')})).pipe(tap(data => {
        const ids = (data.messages || []).map((message: any) => Number(message.idChatMessage));
        // Hydrated pending IDs may be ahead of a bounded catch-up page; only its cursor may advance.
        if (data.nextAfterMessageId != null) latest = Math.max(latest, Number(data.nextAfterMessageId));
        else if (ids.length) latest = Math.max(latest, ...ids);

      }), distinctUntilChanged((a,b) => JSON.stringify(a) === JSON.stringify(b)), map(data => ({...data, chat: normalizeChat(data.chat), messages: (data.messages || []).map(normalizeChat)})));
    }).pipe(shareReplay({bufferSize: 1, refCount: true}));
  }
  changeDetector(): any { return {doc: (path: string) => ({valueChanges: () => this.watchConversation(path).pipe(map(data => data.chat))})}; }
  getMessages(path: string): Observable<any[]> { return this.watchConversation(path).pipe(map(data => data.messages)); }
  getUnreadMessages(path: string): Observable<any[]> {
    return this.http.get<any>('chatRealtime', {params: {...this.scope(path), unreadOnly: 'true', max: '50'}}).pipe(map(data => (data.messages || []).map(normalizeChat)));
  }
  getMessagesPastDays(path: string, before: any): Observable<any[]> {
    if (!before) return new Observable(sub => { sub.next([]); sub.complete(); });
    return this.http.get<any>('chatRealtime', {params: {...this.scope(path), beforeMessageId: String(before.idChatMessage), max: '20'}}).pipe(map(data => (data.messages || []).map(normalizeChat)));
  }
  listChatsByUser(params = {}): Observable<any> { return this.http.get(UrlServer.chat, {params}); }
  show(params: any): Observable<any> { return this.http.get(`${UrlServer.chat}/${params.idChat}`, {params}); }
  showMessageToday(params: any): Observable<any> { return this.http.get(`${UrlServer.historyMessage}/${params.idChat}`, {params}); }
  getUserId(id: number): Observable<any> { return this.http.get(UrlServer.chatGetUsers, {params: {get: String(id)}}); }
  takeChat(payload: any): Observable<any> { return this.http.post(UrlServer.chatSucursal, payload); }
  takeChatAgent(payload: any): Observable<any> { return this.http.post(UrlServer.chatSucursalAgent, payload); }
  sendMessage(payload: any): Observable<any> { return this.http.post(UrlServer.chatSucursal, payload); }
  readMessages(payload: any): Observable<any> { return this.http.post(UrlServer.chatSucursal, payload); }
  closeChat(payload: any): Observable<any> { return this.http.post(UrlServer.chatClose, payload); }
  deleteMessages(payload: any): Observable<any> { return this.http.post(UrlServer.deleteMessage, payload); }
  saveImage(file: File, path: string): AngularFireUploadTask { return this.storage.ref(path).put(file); }
  cancelFile(path: string): Observable<any> { return this.storage.ref(path).delete(); }
  getImageUrl(path: string): Observable<any> { return this.storage.ref(path).getDownloadURL(); }
}
export enum enumStatusChat { SEND = 'SEND', READ = 'READ' }
